import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import Header from '../containers/authenticatedContainer/components/Header';
import Footer from '../containers/authenticatedContainer/components/Footer';
import { useAppSelector } from '../../store';
import Help from '../views/help/Help';

const AuthContainer = lazy(() => import('../containers').then((module) => ({ default: module.AuthContainer })));
const HomePage = lazy(() => import('../views/home-page/HomePage'));
const PageNotFound = lazy(() => import('../views/404/404'));
const CreateSchedule = lazy(() => import('../views/create-schedule/CreateSchedule'));
const ScheduleSummary = lazy(() => import('../components/schedule-summary/scheduleSummary'));
const LandingPage = lazy(() => import('../views/landing-page/LandingPage'));
const Unauthorized = lazy(() => import('../views/unauthorized/unauthorized'));
const UnauthorizedSchedule = lazy(() => import('../views/unauthorizedschedule/unauthorizedschedule'));
const Settings = lazy(() => import('../views/settings/Settings'));
const RateFinder = lazy(() => import('../views/rate-finder/RateFinder'));
const Admin = lazy(() => import('../views/admin/Admin'));
const RiskLocator = lazy(() => import('../components/risk-locator/RiskLocator'));

const getPageTitle = (pathname: string) => {
    if (pathname === '/') return 'IPT Analyzer | Home';
    if (pathname === '/landing-page') return 'IPT Analyzer | Landing Page';
    if (pathname === '/unauthorized') return 'IPT Analyzer | Unauthorized';
    if (pathname === '/unauthorized-schedule') return 'IPT Analyzer | Unauthorized Schedule';
    if (pathname === '/settings') return 'IPT Analyzer | Settings';
    if (pathname === '/rate-finder' || pathname === '/rate-finder-step') return 'IPT Analyzer | Rate Finder';
    if (pathname === '/admin') return 'IPT Analyzer | Admin';
    if (pathname === '/risk-locator') return 'IPT Analyzer | Risk Locator';
    if (pathname === '/help') return 'IPT Analyzer | Help';
    if (pathname.startsWith('/schedule-details')) return 'IPT Analyzer | Create Schedule';
    if (pathname.startsWith('/schedule-summary')) return 'IPT Analyzer | Schedule Summary';

    return 'IPT Analyzer';
};

const RouteTitleManager = () => {
    const location = useLocation();

    useEffect(() => {
        document.title = getPageTitle(location.pathname);
    }, [location.pathname]);

    return null;
};

const AppRoutes = () => {
    const { entitledMenuItems } = useAppSelector().authSlice;
    return (
        <Router>
            <RouteTitleManager />
            <Header />
            <Suspense fallback={<div>...</div>}>
                <Routes>
                    <Route
                        path='/'
                        element={
                            <AuthContainer>
                                <HomePage />
                            </AuthContainer>
                        }
                    />
                    {entitledMenuItems.length > 0 && (
                        <>
                            {entitledMenuItems.some(
                                (menuItem: { title: string }) => menuItem.title.toLowerCase() === 'tax schedule',
                            ) && (
                                <>
                                    <Route
                                        path='/schedule-details'
                                        element={
                                            <AuthContainer>
                                                <CreateSchedule />
                                            </AuthContainer>
                                        }
                                    />
                                    <Route
                                        path='/schedule-details/:scheduleIdEdit'
                                        element={
                                            <AuthContainer>
                                                <CreateSchedule />
                                            </AuthContainer>
                                        }
                                    />
                                </>
                            )}
                            {entitledMenuItems.some(
                                (menuItem: { title: string }) => menuItem.title.toLowerCase() === 'rate finder',
                            ) && (
                                <>
                                    <Route
                                        path='/rate-finder-step'
                                        element={
                                            <AuthContainer>
                                                <RateFinder />
                                            </AuthContainer>
                                        }
                                    />
                                    <Route
                                        path='/rate-finder'
                                        element={
                                            <AuthContainer>
                                                <RateFinder />
                                            </AuthContainer>
                                        }
                                    />
                                </>
                            )}
                            <Route
                                path='/settings'
                                element={
                                    <AuthContainer>
                                        <Settings />
                                    </AuthContainer>
                                }
                            />
                            {entitledMenuItems.some(
                                (menuItem: { title: string }) => menuItem.title.toLowerCase() === 'home',
                            ) && (
                                <Route
                                    path='/schedule-summary/:scheduleId'
                                    element={
                                        <AuthContainer>
                                            <ScheduleSummary />
                                        </AuthContainer>
                                    }
                                />
                            )}
                            {entitledMenuItems.some(
                                (menuItem: { title: string }) => menuItem.title.toLowerCase() === 'risk locator',
                            ) && (
                                <Route
                                    path='/risk-locator'
                                    element={
                                        <AuthContainer>
                                            <RiskLocator />
                                        </AuthContainer>
                                    }
                                />
                            )}
                            <Route
                                path='/insurer-id'
                                element={
                                    <AuthContainer>
                                        <PageNotFound />
                                    </AuthContainer>
                                }
                            />
                            <Route
                                path='/apportionment'
                                element={
                                    <AuthContainer>
                                        <PageNotFound />
                                    </AuthContainer>
                                }
                            />
                            <Route
                                path='/review-publish'
                                element={
                                    <AuthContainer>
                                        <PageNotFound />
                                    </AuthContainer>
                                }
                            />
                            {entitledMenuItems.some((menuItem: { title: string }) => menuItem.title === 'Admin') && (
                                <Route
                                    path='/admin'
                                    element={
                                        <AuthContainer>
                                            <Admin />
                                        </AuthContainer>
                                    }
                                />
                            )}
                            {/* {entitledMenuItems.some((menuItem: { title: string }) => menuItem.title === 'Help') && (
                            )} */}
                            <Route
                                path='/admin'
                                element={
                                    <AuthContainer>
                                        <Admin />
                                    </AuthContainer>
                                }
                            />

                            <Route
                                path='/help'
                                element={
                                    <AuthContainer>
                                        <Help />
                                    </AuthContainer>
                                }
                            />
                        </>
                    )}

                    <Route path='/landing-page' element={<LandingPage />} />
                    <Route path='/unauthorized' element={<Unauthorized />} />
                    <Route path='/unauthorized-schedule' element={<UnauthorizedSchedule />} />
                </Routes>
            </Suspense>
            <Footer />
        </Router>
    );
};

export default AppRoutes;
